import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * Provides the Supabase client + the current auth session to the rest of
 * the SPA. The client is created once (browser singleton); the session is
 * tracked via `onAuthStateChange` so any component reading `useSession()`
 * re-renders on sign-in/out.
 *
 * The anon key here is intended-public. The orchestrator JWT verifier is
 * the authoritative gate; this provider is just the issuance/storage
 * layer.
 */

export interface SupabaseContextValue {
  client: SupabaseClient;
  session: Session | null;
  /** True until the initial `getSession()` resolves. Used by AuthGate to
   * avoid a flash of the sign-in page on a fresh tab where a session
   * actually exists in localStorage. */
  loading: boolean;
}

/** Exported so tests can wrap components in a stub provider value
 * without instantiating the real Supabase client. */
export const SupabaseContext = createContext<SupabaseContextValue | null>(null);

/** Read VITE-injected env. Throws at boot if either is missing — easier
 * to diagnose than a confusing 401 cascade later. */
function readSupabaseEnv(): { url: string; anonKey: string } {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) {
    throw new Error(
      "VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are required. Copy `.env.example` to `.env` and populate from `supabase status`.",
    );
  }
  return { url, anonKey };
}

export function SupabaseProvider({ children }: { children: ReactNode }) {
  const client = useMemo(() => {
    const { url, anonKey } = readSupabaseEnv();
    return createClient(url, anonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false, // we handle the OAuth callback explicitly in AuthCallback
        flowType: "pkce",
      },
    });
  }, []);

  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    client.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session);
      setLoading(false);
    });
    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((_event, next) => {
      setSession(next);
    });
    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [client]);

  const value = useMemo(() => ({ client, session, loading }), [client, session, loading]);
  return <SupabaseContext.Provider value={value}>{children}</SupabaseContext.Provider>;
}

/** Read the current Supabase context. Throws if used outside the provider
 * — that's a programmer error, not a runtime concern. */
export function useSupabase(): SupabaseContextValue {
  const ctx = useContext(SupabaseContext);
  if (!ctx) throw new Error("useSupabase must be used inside SupabaseProvider");
  return ctx;
}

/** Convenience: just the session. */
export function useSession(): Session | null {
  return useSupabase().session;
}
